import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// Small helpers
function toNumber(val) {
  if (val === null || val === undefined || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}
function round2(n) { return Number.isFinite(n) ? Math.round(n * 100) / 100 : n; }
function round5(n) { return Number.isFinite(n) ? Math.round(n * 100000) / 100000 : n; }

function clone(obj) { return JSON.parse(JSON.stringify(obj)); }

// Build reviewed data skeleton from parsed payload for one bill
function buildReviewed(provider, filename, parsedIn) {
  // Normalize in case the backend sends { parsed: {...} } or just {...}
  const parsed = (parsedIn && typeof parsedIn === 'object' && parsedIn.parsed) ? parsedIn.parsed : (parsedIn || {});
  const reviewed = {
    meta: {
      provider: provider || 'MidAmerican',
      filename: filename || '',
      account_number: parsed?.account_number || '',
      service_address: parsed?.service_address || '',
      billing_period_start: parsed?.billing_period_start || '',
      billing_period_end: parsed?.billing_period_end || '',
    },
    summary: {
      current_charges: {
        last_bill: parsed?.current_charges_summary?.last_bill ?? null,
        payments_and_credits: parsed?.current_charges_summary?.payments_and_credits ?? null,
        new_charges: parsed?.current_charges_summary?.new_charges ?? null,
      },
      new_charges_this_month: {
        electric_charges_total: parsed?.new_charges_this_month?.electric_charges_total ?? null,
        gas_charges_total: parsed?.new_charges_this_month?.gas_charges_total ?? null,
        other_charges_total: parsed?.new_charges_this_month?.other_charges_total ?? null,
      },
    },
    usage: {
      electric: {
        monthly_kwh_usage: parsed?.electric_usage?.monthly_kwh_usage ?? null,
        number_of_days: parsed?.electric_usage?.number_of_days ?? null,
        average_temperature: parsed?.electric_usage?.average_temperature ?? null,
      },
      gas: {
        monthly_therm_usage: parsed?.gas_usage?.monthly_therm_usage ?? null,
        number_of_days: parsed?.gas_usage?.number_of_days ?? null,
        average_temperature: parsed?.gas_usage?.average_temperature ?? null,
      },
    },
    details: {
      electric: {
        service_rate: parsed?.detailed_charges?.electric?.service_rate || '',
        items: (parsed?.detailed_charges?.electric?.items || []).map((it) => ({
          description: it.description || '',
          raw_description: it.raw_description || '',
          amount: it.amount ?? 0,
          quantity: it.quantity ?? null,
          unit: it.unit ?? (it.quantity ? 'kWh' : null),
          rate: it.rate ?? null,
          category: it.category || null,
        })),
        subtotal: parsed?.detailed_charges?.electric?.subtotal ?? 0,
        total_reported: parsed?.detailed_charges?.electric?.total_reported ?? 0,
      },
      gas: {
        service_rate: parsed?.detailed_charges?.gas?.service_rate || '',
        items: (parsed?.detailed_charges?.gas?.items || []).map((it) => ({
          description: it.description || '',
          raw_description: it.raw_description || '',
          amount: it.amount ?? 0,
          quantity: it.quantity ?? null,
          unit: it.unit ?? (it.quantity ? 'therm' : null),
          rate: it.rate ?? null,
          category: it.category || null,
        })),
        subtotal: parsed?.detailed_charges?.gas?.subtotal ?? 0,
        total_reported: parsed?.detailed_charges?.gas?.total_reported ?? 0,
      },
      other: parsed?.detailed_charges?.other ? {
        service_rate: parsed?.detailed_charges?.other?.service_rate || '',
        items: (parsed?.detailed_charges?.other?.items || []).map((it) => ({
          description: it.description || '',
          raw_description: it.raw_description || '',
          amount: it.amount ?? 0,
        })),
        subtotal: parsed?.detailed_charges?.other?.subtotal ?? 0,
        total_reported: parsed?.detailed_charges?.other?.total_reported ?? 0,
      } : {
        service_rate: '',
        items: [],
        subtotal: 0,
        total_reported: null,
      },
    },
    reconciliation: {
      sum_of_details: 0,
      equals_current_charges_new_charges: false,
      section_diffs: { electric: 0, gas: 0, other: 0 },
    },
    notes: '',
  };

  // If multiple Electric Energy Charge lines slipped through (rare with current parser), aggregate here to a single row
  try {
    const eItems = reviewed.details.electric.items;
    const energyIdxs = eItems.map((it, idx) => ({ it, idx })).filter(x => x.it.description === 'energy_charge');
    if (energyIdxs.length > 1) {
      const totalAmt = energyIdxs.reduce((s, x) => s + (toNumber(x.it.amount) || 0), 0);
      const totalKwh = toNumber(reviewed.usage.electric.monthly_kwh_usage);
      const rate = totalKwh ? round5(totalAmt / totalKwh) : null;
      // keep first, remove rest
      const firstIdx = energyIdxs[0].idx;
      eItems[firstIdx].amount = round2(totalAmt);
      eItems[firstIdx].quantity = totalKwh || eItems[firstIdx].quantity;
      eItems[firstIdx].unit = 'kWh';
      eItems[firstIdx].rate = rate;
      // delete others
      reviewed.details.electric.items = eItems.filter((_, i) => !energyIdxs.slice(1).some(y => y.idx === i));
    }
  } catch (e) { /* ignore */ }

  return reviewed;
}

export default function UtilityBillReviewModal({ propertyId, results, startIndex = 0, onDone, onCancel }) {
  const [index, setIndex] = useState(startIndex || 0);
  const [reviewed, setReviewed] = useState(() => buildReviewed(results?.[0]?.provider, results?.[0]?.filename, results?.[0]?.parsed));
  const [submitting, setSubmitting] = useState(false);
  const [structures, setStructures] = useState([]);
  const [selectedStructureId, setSelectedStructureId] = useState('');
  const panelRef = React.useRef(null);

  useEffect(() => {
    async function loadStructures(pid) {
      if (!pid) { setStructures([]); setSelectedStructureId(''); return; }
      try {
        // First try linkage-options for nice labels
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
        const data = res.data;
        const children = Array.isArray(data?.children) ? data.children : [];
        let opts = children.filter(ch => (String(ch.type || '').toLowerCase() !== 'land')).map(ch => ({ id: ch.id, label: ch.label || `Structure ${ch.id}` }));
        // Fallback to property details if none
        if (!opts.length) {
          try {
            const propRes = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${pid}/`);
            const propJson = propRes.data;
            const structsArr = Array.isArray(propJson?.structures) ? propJson.structures : [];
            opts = structsArr.map((s, idx) => ({ id: s.id, label: `Structure ${idx + 1}` }));
          } catch (e) { /* ignore */ }
        }
        setStructures(opts);
        if (opts.length === 1) setSelectedStructureId(String(opts[0].id));
      } catch (e) {
        setStructures([]);
        setSelectedStructureId('');
      }
    }
    loadStructures(propertyId);
  }, [propertyId]);

  const current = results?.[index] || null;
  const isAnkenyWater = useMemo(() => {
    const prov = String(current?.provider || '').toLowerCase();
    const utype = String(current?.utility_type || '').toLowerCase();
    return prov === 'city of ankeny' && utype === 'water';
  }, [current]);

  useEffect(() => {
    if (current) {
      setReviewed(buildReviewed(current.provider, current.filename, current.parsed));
    }
  }, [index]);

  const recompute = useMemo(() => {
    const r = clone(reviewed);

    function recalcSection(secKey) {
      const sec = r.details[secKey];
      if (!sec) return { subtotal: 0, diff: 0 };
      // Recompute rates from amount/quantity for electric and gas detail items
      const isElec = secKey === 'electric';
      const isGas = secKey === 'gas';
      const isWater = secKey === 'water';
      const usageQty = isElec ? toNumber(r.usage.electric.monthly_kwh_usage) : (isGas ? toNumber(r.usage.gas.monthly_therm_usage) : null);
      const unit = isElec ? 'kWh' : (isGas ? 'therm' : (isWater ? 'gal' : null));
      let subtotal = 0;
      (sec.items || []).forEach((it) => {
        it.amount = toNumber(it.amount) ?? 0;
        const qty = toNumber(it.quantity);
        if (!qty && usageQty && it.description && (
          (isElec && it.unit === 'kWh') || (isGas && it.unit === 'therm') || (!it.unit && usageQty)
        )) {
          // If quantity is empty but usage is known and this is a per-usage item, assign usage
          it.quantity = usageQty;
          it.unit = unit;
        }
        const denom = toNumber(it.quantity);
        if (denom && denom > 0) {
          it.rate = round5((toNumber(it.amount) || 0) / denom);
        } else {
          // leave rate as-is or null
          it.rate = it.rate === null || it.rate === undefined ? null : toNumber(it.rate);
        }
        subtotal += toNumber(it.amount) || 0;
      });
      sec.subtotal = round2(subtotal);
      const rep = toNumber(sec.total_reported);
      const diff = rep != null ? round2(sec.subtotal - rep) : null;
      return { subtotal: sec.subtotal, diff };
    }

    const e = recalcSection('electric');
    const g = recalcSection('gas');
    const w = r.details.water ? recalcSection('water') : { subtotal: 0, diff: 0 };
    const o = r.details.other ? recalcSection('other') : { subtotal: 0, diff: 0 };

    const parts = [e.subtotal, g.subtotal];
    if (r.details.water) parts.push(w.subtotal);
    if (r.details.other) parts.push(o.subtotal);
    const sumDetails = round2(parts.reduce((s, v) => s + (toNumber(v) || 0), 0));
    const newCharges = toNumber(r.summary.current_charges.new_charges);
    const equalsNew = (newCharges != null) ? (round2(sumDetails - newCharges) === 0) : false;

    r.reconciliation = {
      sum_of_details: sumDetails,
      equals_current_charges_new_charges: !!equalsNew,
      section_diffs: {
        electric: e.diff ?? 0,
        gas: g.diff ?? 0,
        water: r.details.water ? (w.diff ?? 0) : 0,
        other: r.details.other ? (o.diff ?? 0) : 0,
      }
    };

    return r;
  }, [reviewed]);

  if (!current) return null;

  async function handleSubmit() {
    try {
      setSubmitting(true);
      const url = `/api/balance_sheet/assets/physical_assets/resi_properties/${propertyId}/submit-utility-bill/`;
      const payload = {
        provider: current.provider || 'MidAmerican',
        filename: current.filename || '',
        utility_type: current.utility_type || 'electric_gas',
        reviewed: recompute,
        structure_id: selectedStructureId ? Number(selectedStructureId) : null,
      };
      await axios.post(url, payload);
      // advance to next or finish
      if (index + 1 < (results?.length || 0)) {
        setIndex(index + 1);
      } else {
        onDone && onDone();
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Failed to submit reviewed bill');
    } finally {
      setSubmitting(false);
    }
  }

  function setReviewedWithScroll(updateFn) {
    const el = panelRef.current;
    const top = el ? el.scrollTop : null;

    // Capture active input within the panel to preserve caret and focus
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const activeInside = active && el && el.contains(active) ? active : null;
    const keepId = activeInside ? activeInside.getAttribute('data-keep-focus-id') : null;
    let selStart = null, selEnd = null;
    try {
      if (activeInside && typeof activeInside.selectionStart === 'number' && typeof activeInside.selectionEnd === 'number') {
        selStart = activeInside.selectionStart;
        selEnd = activeInside.selectionEnd;
      }
    } catch (e) { /* ignore */ }

    setReviewed(prev => {
      const next = updateFn(prev);
      return next;
    });

    if (el != null) {
      requestAnimationFrame(() => {
        try {
          if (top != null) el.scrollTop = top;
        } catch (e) { /* ignore */ }
        // Restore focus and selection if possible
        try {
          if (keepId) {
            const target = el.querySelector(`[data-keep-focus-id="${keepId}"]`);
            if (target && typeof target.focus === 'function') {
              target.focus();
              if (selStart != null && selEnd != null && typeof target.setSelectionRange === 'function') {
                try { target.setSelectionRange(selStart, selEnd); } catch (e) { /* ignore */ }
              }
            }
          }
        } catch (e) { /* ignore */ }
      });
    }
  }

  function setField(path, value) {
    setReviewedWithScroll(prev => {
      const next = clone(prev);
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  }

  function SectionEditor({ title, secKey, unitLabel }) {
    const sec = recompute.details[secKey];
    if (!sec) return null;

    const isOther = secKey === 'other';

    function addOtherRow() {
      if (!isOther) return;
      setReviewedWithScroll(prev => {
        const next = clone(prev);
        next.details.other = next.details.other || { service_rate: '', items: [], subtotal: 0, total_reported: null };
        next.details.other.items.push({ description: '', raw_description: '', amount: 0 });
        return next;
      });
    }

    return (
      <div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af' }}>
            Reported Total: {sec.total_reported != null ? sec.total_reported.toFixed(2) : '—'} | Subtotal: {sec.subtotal != null ? sec.subtotal.toFixed(2) : '—'} { (sec.total_reported != null) && (recompute.reconciliation.section_diffs[secKey] !== 0) && (
              <span style={{ color:'#f59e0b', marginLeft: 8 }}>(Diff {recompute.reconciliation.section_diffs[secKey].toFixed(2)})</span>
            )}
          </div>
        </div>
        <div style={{ border:'1px solid #334155', borderRadius: 6, overflow: 'hidden' }}>
          {isOther ? (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap: 8, padding: '8px 12px', background:'#0f172a', color:'#cbd5e1', fontWeight:600 }}>
                <div>Category / Description</div>
                <div>Amount ($)</div>
              </div>
              {(sec.items || []).map((it, idx) => (
                <div key={idx} style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap: 8, padding: '8px 12px', borderTop:'1px solid #334155' }}>
                  <div>
                    <input className="form-control" type="text" value={it.raw_description || it.description || ''}
                      data-keep-focus-id={`other.desc.${idx}`}
                      onChange={(e)=> {
                        const val = e.target.value;
                        setReviewedWithScroll(prev => {
                          const next = clone(prev);
                          next.details.other.items[idx].description = val;
                          next.details.other.items[idx].raw_description = val;
                          return next;
                        });
                      }} />
                  </div>
                  <div>
                    <input className="form-control" type="number" step="0.01" value={it.amount ?? ''}
                      data-keep-focus-id={`other.amount.${idx}`}
                      onChange={(e)=> setField(`details.${secKey}.items.${idx}.amount`, toNumber(e.target.value))} />
                  </div>
                </div>
              ))}
              <div style={{ padding: '8px 12px', borderTop:'1px solid #334155', background:'#0f172a' }}>
                <button className="btn btn-sm btn-outline-secondary" onClick={addOtherRow}>+ Add Row</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap: 8, padding: '8px 12px', background:'#0f172a', color:'#cbd5e1', fontWeight:600 }}>
                <div>Description</div>
                <div>Amount ($)</div>
                <div>Quantity ({unitLabel})</div>
                <div>Rate</div>
              </div>
              {(sec.items || []).map((it, idx) => (
                <div key={idx} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap: 8, padding: '8px 12px', borderTop:'1px solid #334155' }}>
                  <div title={it.raw_description}>{it.raw_description || it.description}</div>
                  <div>
                    <input className="form-control" type="number" step="0.01" value={it.amount ?? ''}
                      onChange={(e)=> setField(`details.${secKey}.items.${idx}.amount`, toNumber(e.target.value))} />
                  </div>
                  <div>
                    <input className="form-control" type="number" step="0.01" value={it.quantity ?? ''}
                      onChange={(e)=> setField(`details.${secKey}.items.${idx}.quantity`, toNumber(e.target.value))} />
                  </div>
                  <div>
                    <input className="form-control" type="number" step="0.00001" value={it.rate ?? ''}
                      onChange={(e)=> setField(`details.${secKey}.items.${idx}.rate`, toNumber(e.target.value))} />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex: 1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div className="modal-panel" style={{ width:'min(1200px, 95vw)', maxHeight: '90vh', display:'flex', flexDirection:'column', overflowY:'hidden', background:'#111827', border:'1px solid #374151', borderRadius: 8, color:'#e5e7eb' }}>
        <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ fontSize:18, fontWeight:700 }}>Review Utility Bill ({index + 1} / {results?.length || 1})</div>
          <div style={{ color:'#9ca3af' }}>{current.filename} — {current.provider}</div>
          <button onClick={onCancel} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
        </div>

        {isAnkenyWater ? (
          <>
            <div ref={panelRef} style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:16, padding:16, overflowY:'auto', flex:'1 1 auto' }}>
              {/* Left column: structure selection + summary */}
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ border:'1px solid #334155', borderRadius:6, padding:12 }}>
                  <div className="mb-2">
                    <label className="form-label">Structure</label>
                    <select className="form-select" value={selectedStructureId} onChange={(e)=> setSelectedStructureId(e.target.value)}>
                      <option value="">{structures.length ? 'Select structure…' : 'Loading structures…'}</option>
                      {structures.map(s => (<option key={s.id} value={s.id}>{s.label}</option>))}
                    </select>
                    <small className="form-text text-muted">Bills are linked to a specific structure and will roll up to the property.</small>
                  </div>
                  <div style={{ fontWeight:700, marginBottom:8 }}>City of Ankeny — Water</div>
                  <div style={{ color:'#cbd5e1', fontSize:13 }}>
                    Parsed data will be submitted directly. You can review the raw text and parsed JSON on the right.
                  </div>
                </div>
                {/* Quick parsed summary */}
                <div style={{ border:'1px solid #334155', borderRadius:6, padding:12 }}>
                  <div style={{ fontWeight:700, marginBottom:8 }}>Summary</div>
                  <div style={{ fontSize:13, color:'#cbd5e1' }}>
                    <div><strong>Account:</strong> {current?.parsed?.meta?.account_id || '—'}</div>
                    <div><strong>Period:</strong> {(current?.parsed?.period?.start || '—')} → {(current?.parsed?.period?.end || '—')}</div>
                    <div><strong>Net Usage:</strong> {current?.parsed?.metering?.net_water_usage_gal != null ? `${current.parsed.metering.net_water_usage_gal} gal` : '—'}</div>
                    <div><strong>Total Due:</strong> {current?.parsed?.totals?.total_due != null ? `$${Number(current.parsed.totals.total_due).toFixed(2)}` : '—'}</div>
                    {current?.parsed?.totals?.past_due_charges != null && (
                      <div><strong>Past Due:</strong> ${Number(current.parsed.totals.past_due_charges).toFixed(2)}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right previews: raw text + parsed JSON (read-only) */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <div style={{ fontWeight:700 }}>Raw PDF Text</div>
                  <pre style={{ whiteSpace:'pre-wrap', color:'#e5e7eb', margin:0, background:'#0f172a', padding:12, borderRadius:6, border:'1px solid #334155', maxHeight:'60vh', overflowY:'auto' }}>{current.raw_text || ''}</pre>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <div style={{ fontWeight:700 }}>Parsed (beta)</div>
                  <pre style={{ whiteSpace:'pre-wrap', color:'#e5e7eb', margin:0, background:'#0f172a', padding:12, borderRadius:6, border:'1px solid #334155', maxHeight:'60vh', overflowY:'auto' }}>{JSON.stringify(current.parsed || {}, null, 2)}</pre>
                </div>
              </div>
            </div>

            <div style={{ padding:'12px 16px', borderTop:'1px solid #374151', display:'flex', gap:8 }}>
              <button className="btn btn-secondary" onClick={()=> {
                if (index > 0) setIndex(index - 1);
              }} disabled={index === 0 || submitting}>Back</button>
              <div style={{ marginLeft:'auto' }} />
              <button className="btn btn-outline-secondary" onClick={()=> {
                // Next or Done
                if (index + 1 < (results?.length || 0)) setIndex(index + 1); else onDone && onDone();
              }} disabled={submitting}>Next</button>
              <button className="btn btn-primary" onClick={async ()=>{
                try {
                  setSubmitting(true);
                  const url = `/api/balance_sheet/assets/physical_assets/resi_properties/${propertyId}/submit-utility-bill/`;
                  const payload = {
                    provider: current.provider || 'City of Ankeny',
                    filename: current.filename || '',
                    utility_type: current.utility_type || 'water',
                    // Send the parsed payload directly so backend canonicalizer can map it
                    reviewed: current.parsed || {},
                    structure_id: selectedStructureId ? Number(selectedStructureId) : null,
                  };
                  await axios.post(url, payload);
                  // advance to next or finish
                  if (index + 1 < (results?.length || 0)) {
                    setIndex(index + 1);
                  } else {
                    onDone && onDone();
                  }
                } catch (e) {
                  alert(e.response?.data?.detail || e.message || 'Failed to submit reviewed bill');
                } finally {
                  setSubmitting(false);
                }
              }} disabled={submitting || !selectedStructureId}>{submitting ? 'Submitting…' : 'Submit'}</button>
              <button className="btn btn-secondary" onClick={()=> { onDone && onDone(); }} disabled={submitting}>Close</button>
            </div>
          </>
        ) : (
          <>
            <div ref={panelRef} style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:16, padding:16, overflowY:'auto', flex:'1 1 auto' }}>
              {/* Left summary column */}
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ border:'1px solid #334155', borderRadius:6, padding:12 }}>
                  <div className="mb-2">
                    <label className="form-label">Structure</label>
                    <select className="form-select" value={selectedStructureId} onChange={(e)=> setSelectedStructureId(e.target.value)}>
                      <option value="">{structures.length ? 'Select structure…' : 'Loading structures…'}</option>
                      {structures.map(s => (<option key={s.id} value={s.id}>{s.label}</option>))}
                    </select>
                    <small className="form-text text-muted">Bills are linked to a specific structure and will roll up to the property.</small>
                  </div>
                  <div style={{ fontWeight:700, marginBottom:8 }}>Bill Metadata</div>
                  <div className="mb-2">
                    <label className="form-label">Account Number</label>
                    <input className="form-control" value={reviewed.meta.account_number}
                      onChange={(e)=> setField('meta.account_number', e.target.value)} />
                  </div>
                  <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div>
                      <label className="form-label">Billing Start</label>
                      <input className="form-control" type="date" value={reviewed.meta.billing_period_start}
                        onChange={(e)=> setField('meta.billing_period_start', e.target.value)} />
                    </div>
                    <div>
                      <label className="form-label">Billing End</label>
                      <input className="form-control" type="date" value={reviewed.meta.billing_period_end}
                        onChange={(e)=> setField('meta.billing_period_end', e.target.value)} />
                    </div>
                  </div>
                </div>

                <div style={{ border:'1px solid #334155', borderRadius:6, padding:12 }}>
                  <div style={{ fontWeight:700, marginBottom:8 }}>Current Charges Summary</div>
                  <div className="mb-2">
                    <label className="form-label">Last Bill ($)</label>
                    <input className="form-control" type="number" step="0.01" value={reviewed.summary.current_charges.last_bill ?? ''}
                      onChange={(e)=> setField('summary.current_charges.last_bill', toNumber(e.target.value))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Payments & Credits ($)</label>
                    <input className="form-control" type="number" step="0.01" value={reviewed.summary.current_charges.payments_and_credits ?? ''}
                      onChange={(e)=> setField('summary.current_charges.payments_and_credits', toNumber(e.target.value))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">New Charges ($)</label>
                    <input className="form-control" type="number" step="0.01" value={reviewed.summary.current_charges.new_charges ?? ''}
                      onChange={(e)=> setField('summary.current_charges.new_charges', toNumber(e.target.value))} />
                  </div>
                </div>

                <div style={{ border:'1px solid #334155', borderRadius:6, padding:12 }}>
                  <div style={{ fontWeight:700, marginBottom:8 }}>Auto Reconciliation</div>
                  <div>Sum of Sections: ${recompute.reconciliation.sum_of_details?.toFixed(2)}</div>
                  <div>Equals New Charges? {recompute.reconciliation.equals_current_charges_new_charges ? 'Yes' : 'No'}</div>
                  {(recompute.reconciliation.section_diffs.electric !== 0 || recompute.reconciliation.section_diffs.gas !== 0 || recompute.reconciliation.section_diffs.water !== 0 || recompute.reconciliation.section_diffs.other !== 0) && (
                    <div style={{ color:'#f59e0b', marginTop:6 }}>Section mismatch detected. You can still submit.</div>
                  )}
                </div>
              </div>

              {/* Right details column */}
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <SectionEditor title="Electric" secKey="electric" unitLabel="kWh" />
                <SectionEditor title="Gas" secKey="gas" unitLabel="therm" />
                {recompute.details.water && (
                  <SectionEditor title="Water" secKey="water" unitLabel="gal" />
                )}
                {recompute.details.other && (
                  <SectionEditor title="Other" secKey="other" unitLabel="" />
                )}
              </div>
            </div>

            <div style={{ padding:'12px 16px', borderTop:'1px solid #374151', display:'flex', gap:8 }}>
              <button className="btn btn-secondary" onClick={()=> {
                if (index > 0) setIndex(index - 1);
              }} disabled={index === 0 || submitting}>Back</button>
              <div style={{ marginLeft:'auto' }} />
              <button className="btn btn-outline-secondary" onClick={()=> {
                // Skip this bill
                if (index + 1 < (results?.length || 0)) setIndex(index + 1); else onDone && onDone();
              }} disabled={submitting}>Skip</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
